import { StrictMode } from "react";

import { createRoot } from "react-dom/client";
import { createBrowserRouter } from "react-router";
import { RouterProvider } from "react-router/dom";

import { QueryClientProvider } from "@tanstack/react-query";

import queryClient from "@/shared/lib/utils/queryClient";

import { TooltipProvider } from "./components/ui/tooltip";
import "./main.css";
import AppLayout, { AppLayoutError, AppLayoutLoading, appLayoutLoader } from "./pages/AppLayout";
import CreateTicket from "./pages/CreateTicket";
import EditTicket from "./pages/EditTicket";
import Home from "./pages/Home";
import Login, { loginLoader } from "./pages/Login";
import Notifications from "./pages/Notifications";
import Profile from "./pages/Profile";
import ProjectLayout, { ProjectLayoutError, ProjectLayoutLoading, projectLayoutLoader } from "./pages/ProjectLayout";
import ProjectMembers from "./pages/ProjectMembers";
import ProjectOverview from "./pages/ProjectOverview";
import ProjectSettings from "./pages/ProjectSettings";
import ProjectTickets from "./pages/ProjectTickets";
import Projects from "./pages/Projects";
import Settings from "./pages/Settings";
import SignUp, { signUpLoader } from "./pages/SignUp";
import Ticket, { TicketError, TicketLoading, ticketLoader } from "./pages/Ticket";
import { AuthenticationProvider } from "./shared/contexts/AuthenticationContext";

const router = createBrowserRouter([
  {
    path: "account/sign-up",
    element: <SignUp />,
    loader: signUpLoader,
    hydrateFallbackElement: <AppLayoutLoading />,
    errorElement: <AppLayoutError />,
  },
  {
    path: "account/login",
    element: <Login />,
    loader: loginLoader,
    hydrateFallbackElement: <AppLayoutLoading />,
    errorElement: <AppLayoutError />,
  },
  {
    element: <AppLayout />,
    loader: appLayoutLoader,
    hydrateFallbackElement: <AppLayoutLoading />,
    errorElement: <AppLayoutError />,
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        path: "notifications",
        element: <Notifications />,
      },
      {
        path: "profile",
        element: <Profile />,
      },
      {
        path: "settings",
        element: <Settings />,
      },
      {
        path: "projects",
        element: <Projects />,
      },
      {
        path: "projects/:id",
        element: <ProjectLayout />,
        loader: projectLayoutLoader,
        hydrateFallbackElement: <ProjectLayoutLoading />,
        errorElement: <ProjectLayoutError />,
        children: [
          {
            index: true,
            element: <ProjectOverview />,
          },
          {
            path: "tickets",
            element: <ProjectTickets />,
          },
          {
            path: "tickets/new",
            element: <CreateTicket />,
          },
          {
            path: "tickets/:ticketId",
            element: <Ticket />,
            loader: ticketLoader,
            hydrateFallbackElement: <TicketLoading />,
            errorElement: <TicketError />,
          },
          {
            path: "tickets/:ticketId/edit",
            element: <EditTicket />,
            loader: ticketLoader,
            hydrateFallbackElement: <TicketLoading />,
            errorElement: <TicketError />,
          },
          {
            path: "members",
            element: <ProjectMembers />,
          },
          {
            path: "settings",
            element: <ProjectSettings />,
          },
        ],
      },
    ],
  },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <TooltipProvider delayDuration={800}>
      <QueryClientProvider client={queryClient}>
        <AuthenticationProvider>
          <RouterProvider router={router}></RouterProvider>
        </AuthenticationProvider>
      </QueryClientProvider>
    </TooltipProvider>
  </StrictMode>
);
